// src/features/media/PendingTextTracksStore.ts

import { TextTrackUtils } from './TextTrackUtils';
import type { PendingTextTrack } from './TextTrackUtils';
import type { ITextTrackData } from '@/lib/types/Media';

const EMPTY: PendingTextTrack[] = [];

/**
 * Session-scoped overlay of caption tracks that exist server-side but are not
 * yet returned by the media view (the view excludes non-ready tracks, so a
 * freshly created track is invisible to refetches until ingest completes).
 * Entries are added from the create response (real fileId) and removed when
 * the server confirms the track or the user deletes it. A page reload drops
 * the overlay, matching server visibility.
 */
class PendingTextTracksStore {
  private byMedia = new Map<number, PendingTextTrack[]>();
  private listeners = new Set<() => void>();

  // Arrow fn: passed directly to useSyncExternalStore
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /** Stable reference until the media's entries change (useSyncExternalStore contract). */
  getForMedia(mediaId: number): PendingTextTrack[] {
    return this.byMedia.get(mediaId) ?? EMPTY;
  }

  add(mediaId: number, track: ITextTrackData): void {
    const current = this.byMedia.get(mediaId) ?? EMPTY;
    this.byMedia.set(mediaId, [...current, { track, addedAtMs: Date.now() }]);
    this.notify();
  }

  remove(mediaId: number, fileId: number): void {
    const current = this.byMedia.get(mediaId);
    if (!current) return;
    const next = current.filter(p => p.track.fileId !== fileId);
    if (next.length !== current.length) {
      this.byMedia.set(mediaId, next);
      this.notify();
    }
  }

  /** Drop entries the server now returns. */
  removeConfirmed(mediaId: number, serverTracks: ITextTrackData[]): void {
    const current = this.byMedia.get(mediaId);
    if (!current || current.length === 0) return;
    const next = current.filter(p => !TextTrackUtils.isConfirmed(p.track, serverTracks));
    if (next.length !== current.length) {
      this.byMedia.set(mediaId, next);
      this.notify();
    }
  }

  private notify(): void {
    this.listeners.forEach(listener => listener());
  }
}

export const pendingTextTracksStore = new PendingTextTracksStore();
